/**
 * @fileoverview Input/output schemas for chapter-rebuilder-v1.
 *
 * This agent updates chapter metadata (keyEvents, chapterGoals) based on the
 * actual written chapter content. It treats the chapter content as the source
 * of truth and conservatively updates only factual differences.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { ChapterGoalsSchema } from '@/domains/chapter-workspace/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.knownCharacters.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema for chapter-rebuilder-v1.
 */
export const OutputSchema = z.object({
  commentary: z.string().describe(
    'A brief summary of the changes made. Explain what was updated and why (2-4 sentences).'
  ),
  keyEvents: z.string().describe(
    'Updated key events reflecting what actually happens in the chapter. Neutral summary — no dialogue, no sensory details, objective style. Target approximately one paragraph per 1000 projected words; if existing keyEvents are more detailed, preserve that level of detail.'
  ),
  chapterGoals: ChapterGoalsSchema.describe(
    'The updated chapter goals based on the actual written content.'
  ),
  suggestedPacing: z.enum([
    'Slow & Reflective',
    'Moderate & Developing',
    'Fast & Action-Packed',
  ]).optional().describe(
    'Suggested pacing if the written content differs significantly from planned. Only include if a change is warranted.'
  ),
  suggestedTitle: z.string().optional().describe(
    'Suggested title if the chapter scope shifted significantly. Only include if the current title no longer fits.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
